/* eslint react/no-danger: 0 */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import * as Cookies from 'js-cookie';

import './SingleJobTemplate.scss';

import {
    JobContextProvider,
    JobContextConsumer,
} from '../../../contexts/JobContext';
import { ATTRIBUTION_MODE_CHERRYPICK } from '../../../contexts/AbstractContext';
import { Consumer as PageConsumer } from '../../../contexts/PageContext';
import { convincerPropType } from '../../../components/JobV3/Elements/ConvincerBox/constants';
import { googleAnalytic } from '../../googeAnalyticHelper';
import { COMPANY_PROPTYPES } from '../../../components/Includes/CompanyProps';
import { jobDetailsPropType } from '../../../components/Includes/JobProps';
import { buildMetrics } from '../../../components/JobV3/Elements/JobListing/constants';
import { EmitMetric } from '../../../components/Analytics/VisibilityPixel/VisibilityPixel';
import Layout from '../../../components/Layout/Layout';
import ScrollEventStatusProvider from '../../../components/JobV3/SingleJob/ScrollEventStatusProvider/ScrollEventStatusProvider';
import { retrieveSearchTrackingCookieData } from '../../../components/JobV3/searchEmitters';
import StickyCompanyHeaderV2 from '../../../components/JobV3/SingleJob/Layout/StickyCompanyHeader/StickyCompanyHeaderV2/StickyCompanyHeaderV2';
import { ButtonFooterV2 } from '../../../components/JobV3/SingleJob/Layout/ButtonFooter/ButtonFooterV2';
import { SingleJobTemplateV2 } from '../../../components/JobV3/SingleJob/SingleJobTemplateV2/SingleJobTemplateV2';
import {
    SnowplowPageViewTracker,
    PAGE_VIEW_SECTIONS,
    PAGE_VIEW_TYPES,
} from '../../../components/Analytics/Snowplow/SnowplowPageViewTracker';
import { PageContext } from '../../../contexts/ContextCreator';
import { Footer } from '../../../components/Layout/Footer/DesktopFooter/DesktopFooterV2/DesktopFooterV2';

export const SingleJobTemplate = (props) => {
    const {
        PAGE_PROPS,
        googleJobStructuredData,
        companyReviewStructuredData,
        shouldShowStickyNav,
        topApplyButtonGroupRef,
        bottomApplyButtonGroupRef,
        user,
    } = props;
    const { setLoginWallWithDescriptionText } = useContext(PageContext);
    const cookieBannerHeight = 110;

    const getJobSearchQueryID = () => {
        const { queryID, placedHash } = retrieveSearchTrackingCookieData();

        if (placedHash === props.jobDetails.slug) {
            return queryID;
        }

        return null;
    };

    const getCTSJobName = () => {
        const { placedHash, ctsName } = retrieveSearchTrackingCookieData();

        if (placedHash === props.jobDetails.slug && ctsName) {
            return [ctsName];
        }
        return null;
    };

    const handleCompanyApplyClick = (position = '') => {
        if (window) {
            const clickMetrics = buildMetrics({
                type: 'misc_event_count',
                originalApplyUrl: props.jobDetails.applyUrl,
                category: props.jobDetails.category,
                company: {
                    id: props.company.companyId,
                    name: props.company.companyName,
                },
                hash: props.jobDetails.slug,
                title: props.jobDetails.title,
                location: props.jobDetails.locationName,
                jobSearchQueryID: getJobSearchQueryID(),
                position: null,
                miscEventType: `${position}-apply-job-button-click`,
                ctsName: getCTSJobName(),
                ctsEvent: 'APPLICATION_REDIRECT',
            });

            EmitMetric(clickMetrics);

            googleAnalytic('Click', 'JobsV3', props.company.companyName);
            const newTab = window.open(props.jobDetails.applyUrl, '_blank');
            newTab?.focus();
            if (!user) {
                Cookies.set('EVENT-LOGIN-TRIGGER', 'single-job-page', {
                    expires: 1095,
                });
                setLoginWallWithDescriptionText({
                    title: 'Get jobs straight to your inbox',
                    description:
                        'See which companies want to hire more women like you.',
                });
                EmitMetric({
                    misc_event_type: 'after-apply-signup',
                    misc_event_count: 1,
                    test_flag: 'Single Job Pages',
                });
            }
        }
    };

    const getApplyButtonProps = () => ({
        handleCompanyApplyClick,
    });

    const applyButtonProps = getApplyButtonProps();

    return (
        <div>
            <SnowplowPageViewTracker
                pageCompanyId={props.company.companyId}
                pageEntityId={props.jobDetails.slug}
                pageSection={PAGE_VIEW_SECTIONS.JOB_DETAIL}
                pageType={PAGE_VIEW_TYPES.CONTENT}
            />

            {googleJobStructuredData && (
                <div
                    className="google-amp"
                    dangerouslySetInnerHTML={{
                        __html: `<script type='application/ld+json'>${googleJobStructuredData}</script>`,
                    }}
                />
            )}
            {companyReviewStructuredData && (
                <div
                    className="google-cReview-structured"
                    dangerouslySetInnerHTML={{
                        __html: `<script type='application/ld+json'>${companyReviewStructuredData}</script>`,
                    }}
                />
            )}
            <StickyCompanyHeaderV2
                visible={shouldShowStickyNav}
                {...applyButtonProps}
                {...props}
            />

            <Layout
                PAGE_PROPS={PAGE_PROPS}
                displayDesktopFooter={false}
                className="single-job-page-layout v2-template"
                removeCookieBanner
            >
                <SingleJobTemplateV2
                    topApplyButtonGroupRef={topApplyButtonGroupRef}
                    bottomApplyButtonGroupRef={bottomApplyButtonGroupRef}
                    handleCompanyApplyClick={
                        applyButtonProps?.handleCompanyApplyClick
                    }
                    hasCookie
                    cookieBannerHeight={cookieBannerHeight}
                    {...props}
                />
                <ButtonFooterV2
                    visible={shouldShowStickyNav}
                    hasCookie
                    cookieBannerHeight={cookieBannerHeight}
                    {...applyButtonProps}
                    {...props}
                />
            </Layout>
            <Footer />
        </div>
    );
};

const SingleJobTemplateWithScrollEvent = (props) => (
    <ScrollEventStatusProvider>
        {({
            topApplyButtonGroupRef,
            bottomApplyButtonGroupRef,
            shouldShowStickyNav,
        }) => (
            <SingleJobTemplate
                {...props}
                topApplyButtonGroupRef={topApplyButtonGroupRef}
                bottomApplyButtonGroupRef={bottomApplyButtonGroupRef}
                shouldShowStickyNav={shouldShowStickyNav}
            />
        )}
    </ScrollEventStatusProvider>
);

export default (props) => {
    const jobContent = {
        ...props.jobDetails,
        hash: props.jobDetails.slug,
        type: 'jobbing',
    };

    return (
        <PageConsumer>
            {({ pageProps: { featureFlags }, user, jobProfileSnippet }) => (
                <JobContextProvider
                    attributionMode={ATTRIBUTION_MODE_CHERRYPICK}
                    content={[jobContent]}
                    location="single-jobs-page"
                    userJobProfile={props.userJobProfile}
                >
                    <JobContextConsumer>
                        {(jobContext) => (
                            <SingleJobTemplateWithScrollEvent
                                {...props}
                                jobContext={jobContext}
                                user={!!user}
                                featureFlags={featureFlags}
                                jobProfileSnippet={jobProfileSnippet}
                            />
                        )}
                    </JobContextConsumer>
                </JobContextProvider>
            )}
        </PageConsumer>
    );
};

SingleJobTemplate.propTypes = {
    /**
     * company info for company page layout
     */
    company: PropTypes.shape(COMPANY_PROPTYPES).isRequired,
    /**
     * job data for single job page
     */
    jobDetails: jobDetailsPropType,
    /**
     * data provided by jobContext
     */
    jobContext: PropTypes.oneOf(PropTypes.object).isRequired,
    /**
     * ref for top apply button section, use to calculate elelement offset for showing/hiding sticky company info header
     */
    topApplyButtonGroupRef: PropTypes.shape({
        current: PropTypes.oneOf(PropTypes.object),
    }),
    /**
     * ref for bottom apply button section, use to calculate elelement offset for showing/hiding sticky company info header
     */
    bottomApplyButtonGroupRef: PropTypes.shape({
        current: PropTypes.oneOf(PropTypes.object),
    }),
    /**
     * whether should show sticky company header and ButtonFooter or not
     */
    shouldShowStickyNav: PropTypes.bool.isRequired,
    /**
     * whether should show job profile form or not
     */
    convincers: PropTypes.arrayOf(convincerPropType),
    jobProfileSnippet: PropTypes.exact({
        hash: PropTypes.string,
        isCompleted: PropTypes.bool.isRequired,
    }).isRequired,
    featureFlags: PropTypes.oneOf(PropTypes.object),
    PAGE_PROPS: PropTypes.oneOf(PropTypes.object),
    googleJobStructuredData: PropTypes.string,
    companyReviewStructuredData: PropTypes.string,
    user: PropTypes.bool,
};

SingleJobTemplate.defaultProps = {
    jobDetails: {},
    convincers: null,
    topApplyButtonGroupRef: null,
    bottomApplyButtonGroupRef: null,
    googleJobStructuredData: null,
    companyReviewStructuredData: null,
    user: null,
    featureFlags: {},
    PAGE_PROPS: {},
};
